import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

/**
 * The full-screen "Switching to … / Welcome to …" card. Shared by the client
 * switch (which reloads the app between the two phases) and the workspace
 * switch (which navigates in place), so both read as the same motion:
 * 300ms fade and zoom in, a ring spinning around the tile while leaving,
 * 500ms fade out.
 *
 * The stack is centered, so anything mounted only in one phase would
 * re-center it when the phase flips and nudge the title. The loading ring is
 * therefore an absolute overlay on the visual that is always mounted and only
 * fades, keeping the card's box identical in both phases.
 */
export function SwitchOverlayCard({
  visual,
  title,
  phase,
  exiting = false,
}: {
  visual: ReactNode
  title: string
  phase: "leaving" | "arriving"
  exiting?: boolean
}) {
  return (
    <div
      aria-live="polite"
      role="status"
      className={cn(
        "fixed inset-0 z-[200] flex items-center justify-center bg-background/95 backdrop-blur-sm",
        exiting
          ? "motion-safe:animate-out motion-safe:fade-out motion-safe:duration-500 opacity-0 transition-opacity duration-500"
          : "motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
      )}
    >
      <div
        className={cn(
          "flex flex-col items-center gap-4",
          !exiting && "motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-300"
        )}
      >
        <div className="relative">
          {visual}
          <span
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute -inset-2.5 rounded-full border-2 border-primary/15 border-t-primary transition-opacity duration-300",
              "motion-safe:animate-[spin_0.9s_linear_infinite] motion-reduce:border-primary/40",
              phase === "leaving" ? "opacity-100" : "opacity-0"
            )}
          />
        </div>
        <div className="text-center">
          <div className="text-sm text-muted-foreground">
            {phase === "leaving" ? "Switching to" : "Welcome to"}
          </div>
          <div className="text-lg font-semibold">{title}</div>
        </div>
      </div>
    </div>
  )
}
